import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components'
import { useTable, usePagination, useSortBy, useFilters } from 'react-table'
import ReactLoading from 'react-loading';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import dayjs from 'dayjs';

import fr from 'date-fns/locale/fr-CA';
import en from 'date-fns/locale/en-CA';
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";

import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';


import {
  getPonctualiteRoutes,
  getAllAgencies,
  getUser, getUserAgency
} from '../API';

import { useTranslation } from "react-i18next";

const Styles = styled.div
  `
  padding: 1rem;

  .pagination {
    padding: 0.5rem;
  }

  td { background-color: white; }
  
`

function Table({ columns, data }) {

  const [t] = useTranslation('common');

  const [filterInput, setFilterInput] = useState("");

  const handleFilterChange = e => {
    const value = e.target.value || undefined;
    setFilter("route_id", value); // Update this specific field
    setFilterInput(value);
  };

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active pagconst [cellValue, setCellValue] = useState('');e

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useFilters,
    useSortBy,
    usePagination,
  )


  // Render the UI for your table
  return (


    <>
      <input
        value={filterInput}
        onChange={handleFilterChange}
        placeholder={t("routeoperation.find")}
      />
      <table className="table" {...getTableProps()}>
        <thead style={{ textAlign: 'center' }}>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                // Add the sorting props to control sorting. For this example
                // we can add them into the header props
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {/* Add a sort direction indicator */}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {

            prepareRow(row);

            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {/* 
        Pagination can be built however you'd like. 
        This is just a very basic UI implementation:
      */}
      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {'<<'}
        </button>{' '}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {'<'}
        </button>{' '}
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {'>'}
        </button>{' '}
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {'>>'}
        </button>{' '}
        <span>
          Page{' '}
          <strong>
            {pageIndex + 1} {t('routeoperation.of')} {pageOptions.length}
          </strong>{' '}
        </span>
        <span>
          | {t("routeoperation.go")}{' '}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={e => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0
              gotoPage(page)
            }}
            style={{ width: '100px' }}
          />
        </span>{' '}
        <select
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value))
          }}
        >
          {[25, 50, 100].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              {t("routeoperation.show")} {pageSize}
            </option>
          ))}
        </select>
      </div>
    </>
  )
}


function PonctualiteRoute() {

  const [t] = useTranslation('common');
  const { i18n } = useTranslation('home');

  const columns = useMemo(
    () => [
      {
        Header: t('routeotp.table.route'),
        accessor: 'route_id',
        Cell: props => <div>{props.value}</div>,
      },
      // {
      //   Header: 'Type de service',
      //   accessor: 'service_id',
      // },
      {
        Header: t('routeotp.table.ontime'),
        accessor: 'ponctuel',
        Cell: props => <div>{props.value.toFixed(1)+'%'}</div>,
      },
      {
        Header: t('routeotp.table.prevontime'),
        accessor: 'prevponc',
        Cell: ({ value, row }) => {
          const current = row.original.ponctuel;

          return (
            <div style={{ alignItems: 'center'}}>
              <span>{value ? (current-value).toFixed(1)+'%' : ''}</span>

              {current > value && (
                <ArrowUpwardIcon sx={{ color: 'success.main', fontSize: 18 }} />
              )}

              {current < value && (
                <ArrowDownwardIcon sx={{ color: 'error.main', fontSize: 18 }} />
              )}
            </div>
          );
        }
        //Cell: props => <div> {props.value ? props.value.toFixed(1)+'%' : ''} </div>
      },
      {
        Header: t('routeotp.table.late'),
        accessor: 'retard',
        Cell: props => <div> {props.value.toFixed(1)}% </div>
      },
      {
        Header: t('routeotp.table.early'),
        accessor: 'avance',
        Cell: props => <div> {props.value.toFixed(1)}% </div>
      },
      {
        Header: <div>{t('routeotp.table.sample1')}<br /><div>{t('routeotp.table.sample2')}</div></div>,
        accessor: 'realtrips',
      },
      {
        Header: <div>{t('routeotp.table.planned1')}<br /><div>{t('routeotp.table.planned2')}</div></div>,
        accessor: 'plantrips',
      },
      {
        Header: <div>{t('routeotp.table.rate1')}<br /><div>{t('routeotp.table.rate2')}</div></div>,
        accessor: 'pcttrips',
        Cell: props => <div> {(props.value).toFixed(1)}% </div>
      },
      {
        Header: <div>{t('routeotp.table.var1')}<br /><div>{t('routeotp.table.var2')}</div></div>,
        accessor: 'var',
        Cell: props => <div> {(props.value / 60).toFixed(1)} </div>
      }
    ],
    []
  )

  const [loading, setLoading] = useState(0);

  const [startDate, setStartDate] = useState(new Date().setDate(new Date().getDate() - 1));
  const [endDate, setEndDate] = useState(null);
  const [startFormat, setStartFormat] = useState(dayjs().add(-8, 'day').format('YYYY-MM-DD'));
  const [endFormat, setEndFormat] = useState(dayjs().add(-1, 'day').format('YYYY-MM-DD'));
  const [allDates, setAllDates] = useState(0);

  const [user, setUser] = useState('');
  const [agency, setAgency] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [userAgency, setUserAgency] = useState('');

  const [routes, setRoutes] = useState([]);

  const [service, setService] = useState("SE");

  const [refreshKey, setRefreshKey] = useState(0);

  // Sans clé dans l'array, la fonction est exécutée 1 seule fois
  useEffect(() => {
    getAllAgencies()
      .then(response => {
        setAgencies(response);
      })
    getUser()
      .then(response => {
        setUser(response.user);
      })
    getUserAgency()
      .then(response => {
        setUserAgency(response);
      })
  }, []);

  useEffect(() => {
    setLoading(1);

    getPonctualiteRoutes(agency, service, startFormat, endFormat)
      .then(response => {

        setRoutes(response);
        setLoading(0);
      });

  }, [refreshKey]);


  async function handleServiceClick(e) {
    if (e.target.innerHTML == t('dailyotp.week')) {
      setService("SE")
    } else if (e.target.innerHTML == t('dailyotp.sat')) {
      setService("SA")
    } else {
      setService("DI")
    }
    setRefreshKey(oldKey => oldKey + 1);
  }

  async function handleAgencySelect(e) {
    setAgency(e.target.value);
    setRefreshKey(oldKey => oldKey + 1);
  }

  const handleChangeDate = (dates) => {

    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (end !== null) {
      let formatstart = dayjs(start).format('YYYY-MM-DD');
      let formatend = dayjs(end).format('YYYY-MM-DD');
      setAllDates(0);
      setStartFormat(formatstart);
      setEndFormat(formatend);
      setRefreshKey(oldKey => oldKey + 1);
    }

  };

  const handleDateInit = () => {
    setStartDate(new Date().setDate(new Date().getDate() - 1));
    setEndDate(null);
    setStartFormat('');
    setEndFormat('');
    setAllDates(1);
    setRefreshKey(oldKey => oldKey + 1);
  };

  const toPercent = (decimal) => `${(decimal).toFixed(0)}%`;

  const CustomTooltip = ({ active, payload }) => {

    if (active, payload) {
      return (
        <div className="custom-tooltip">
          <p className="label">{t('vehoperation.graphs.hover.route')} {payload[0] ? payload[0].payload.route_id : ''}<br />
            {t('dailyotp.hover.ontime')} {payload[0] ? payload[0].payload.ponctuel.toFixed(0) : ''}% <br />
            {t('dailyotp.hover.late')} {payload[0] ? payload[0].payload.retard.toFixed(0) : ''}% <br />
            {t('dailyotp.hover.early')} {payload[0] ? payload[0].payload.avance.toFixed(0) : ''}%
          </p>
        </div>
      );
    }
    return null;
  };


  return (
    routes ?
      <>

        <div className="row justify-content-center ps-1 pe-1">
          <div className="col-sm"></div>
          <h1 id="page-title" className="display-5">{t('routeotp.title')}</h1>
          {Array.isArray(userAgency) && userAgency.length > 1 ? <div className="row justify-content-center mb-2">
            <h3 id="page-subtitle" className="display-5"><b>{agency != '' ? agency.toUpperCase() : userAgency[0].toUpperCase()}</b> </h3>
          </div> : ''}
        </div>

        <div className="container" style={{ backgroundColor: "#f8f9fa" }}>

          {/* <div className="row justify-content-center mb-2">
            <h4 id="page-subtitle">Points récupérés aux 90 secondes</h4>
          </div> */}

          <div className="row justify-content-center pt-3">
            <div className="col text-center">
              <div className="me-1 border rounded ps-2 pe-2" style={{ backgroundColor: "white" }}>

                <div className="row text-center pt-3 pb-2">
                  <DatePicker
                    selected={startDate}
                    onChange={handleChangeDate}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    inline
                    locale={i18n.language.substring(0, 2) == 'fr' ? fr : en}
                    excludeDateIntervals={[
                      {
                        start: subDays(new Date('2000-01-01'), 0),
                        end: addDays(new Date('2023-07-01'), 0)
                      },
                      {
                        start: subDays(new Date(), 1),
                        end: addDays(new Date('2030-12-31'), 0)
                      },
                    ]}
                  />
                </div>

                <div className="row justify-content-center">
                  <div className="col text-center pt-2 pb-2">
                    <h3>{(startFormat == endFormat) && allDates !== 1 ?
                      dayjs(startFormat).format('YYYY-MM-DD') :
                      allDates == 1 ? t('tripotp.alldates') :
                        t('vehdays.from') + dayjs(startFormat).format('YYYY-MM-DD') + t('vehdays.to') + dayjs(endFormat).format('YYYY-MM-DD')}</h3>
                  </div>
                </div>

                <div className="row justify-content-center">
                  <div className="col text-center pb-3">
                    <button type="button" className="btn btn-secondary"
                      onClick={handleDateInit}>{t('routeotp.alldates')}</button>
                  </div>
                </div>

                {Array.isArray(userAgency) && userAgency.length > 1 ? <div className="row justify-content-center">
                  <div className="col-lg-4 col-sm-12">
                    <select onChange={(event) => handleAgencySelect(event)} className="form-select form-select-lg mb-3 mt-3">
                      <option defaultValue>Opérateur</option>
                      {userAgency.map((e) => (
                        <option value={e} key={e}
                        >{e}</option>
                      ))}
                    </select>
                  </div>
                </div> : ''}

                <div className="row">
                  <div className="col text-center pb-3">
                    <div type="button" className={service == "SE" ? "btn btn-dark me-1 mt-1" : "btn btn-outline-dark me-1 mt-1"}
                      onClick={(event) => handleServiceClick(event)}>{t('dailyotp.week')}</div>
                    <div type="button" className={service == "SA" ? "btn btn-dark me-1 mt-1" : "btn btn-outline-dark me-1 mt-1"}
                      onClick={(event) => handleServiceClick(event)}>{t('dailyotp.sat')}</div>
                    <div type="button" className={service == "DI" ? "btn btn-dark me-1 mt-1" : "btn btn-outline-dark me-1 mt-1"}
                      onClick={(event) => handleServiceClick(event)}>{t('dailyotp.sun')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loading == 0 ? '' :
            <div className="ponctualitespinner">
              <ReactLoading type={"spin"} color={"#4d0077"} height={225} width={125} />
            </div>}

          <div className="row justify-content-center pt-3 pb-3">
            <div className="col-lg col-sm-12 pt-3">
              <div className="me-1 border rounded" style={{ backgroundColor: "white" }}>

                <div className="row justify-content-center align-items-center">
                  <div className="col-lg-12 text-center">
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        width={500}
                        height={300}
                        data={routes}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="route_id" angle={-90} dx={-4} textAnchor="end" height={50} />
                        <YAxis tickFormatter={toPercent} domain={[0, 100]} />
                        <Tooltip
                          filterNull={false}
                          content={<CustomTooltip />}
                        />
                        <Legend formatter={(value, entry, index) =>
                          <span style={{ color: "black" }} className="text-color-class">{value}</span>
                        } />
                        <Bar name={t('dailyotp.legend.ontime')} dataKey="ponctuel" stackId="a" fill="#0c8912" />
                        <Bar name={t('dailyotp.legend.late')} dataKey="retard" stackId="a" fill="#eeda04" />
                        <Bar name={t('dailyotp.legend.early')} dataKey="avance" stackId="a" fill="#e1341e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row justify-content-center pt-3 pb-3">
            <div className="col-lg col-sm-12 text-center pt-2">
              <div className="me-1 border rounded ps-2 pe-2" style={{ backgroundColor: "white" }}>
                <div id="overflow-table" className="container">
                  <div className="row justify-content-center">
                    <div className="col-12">
                      <Styles>
                        <Table columns={columns} data={routes} />
                      </Styles>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </> :
      <>
        <div className="d-flex justify-content-center">
          <div className="row">
            <div className="col-sm">
              <h1 className="display-5">{t('data.loading')}</h1>
              <div className="d-flex justify-content-center">
                <ReactLoading type={"bars"} color={"#4d0077"} height={300} width={175} />
              </div>
            </div>
          </div>
        </div>
      </>
  )
}

export default PonctualiteRoute